import { AdaChatBotProvider } from '@mews-commander/vendor/ada-chat-bot';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { ArticlePath, createGuideLink } from '@mews-commander/enums';
import { PageLayout, SmallContentContainer, useBreadcrumbs } from '@mews-commander/ui';
import { Divider, Stack, Typography } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { Authentication } from '../Authentication';
import { DeviceAuthConfiguration } from '../DeviceAuthConfiguration/DeviceAuthConfiguration';
import { ScimConfiguration } from '../ScimConfiguration/ScimConfiguration';
import { SsoConfiguration } from '../SsoConfiguration/SsoConfiguration';
import { useOwnerIdQueryPayload } from '../utils/useOwnerId';
import { SecurityBadge } from './SecurityBadge';
import { SecurityTips } from './SecurityTips';
import { SecurityOverview } from './SecurityOverview';
import { useSecurityDashboardFlags } from './useSecurityDashboardFlags';
import { SecurityOverviewSkeleton } from './SecurityOverviewSkeleton';

export const SecurityDashboard = () => {
    const breadcrumbs = useBreadcrumbs();

    const { isPortfolioWithSsoSubscription } = useSecurityDashboardFlags();
    const ownerIdPayload = useOwnerIdQueryPayload();

    const { data } = useRepositoryQuery('securityOverview', 'getOverview', ownerIdPayload);

    return (
        <PageLayout breadcrumbs={breadcrumbs} guideLink={createGuideLink(ArticlePath.SecurityOverview)} padded>
            <SmallContentContainer condensed>
                <AdaChatBotProvider>
                    <Stack spacing="300" vertical>
                        <Stack spacing="200" vertical>
                            {data ? (
                                <>
                                    <Stack
                                        spacing="100"
                                        itemAlignment="center"
                                        distribution="space-between"
                                        phoneOnly={{ vertical: true, itemAlignment: 'start' }}
                                    >
                                        <Typography textStyle="titleMediumStrong" as="h1">
                                            {lt('SecurityOverviewTitle')}
                                        </Typography>
                                        <SecurityBadge level={data.securityOverviewBadge} />
                                    </Stack>
                                    <SecurityOverview data={data} />
                                </>
                            ) : (
                                <SecurityOverviewSkeleton />
                            )}

                            <SecurityTips level={data?.securityOverviewBadge} />
                        </Stack>
                        <Divider />

                        <SecurityDashboardSection title={lt('SecurityOverviewTitleSecurityFeatures')}>
                            <SsoConfiguration isEnabled={data?.ssoEnabled} isRequired={data?.ssoAllDomainsEnforced} />
                            <Authentication />
                            <DeviceAuthConfiguration />
                        </SecurityDashboardSection>

                        {isPortfolioWithSsoSubscription && (
                            <SecurityDashboardSection title={lt('UserProvisioning')}>
                                <ScimConfiguration />
                            </SecurityDashboardSection>
                        )}
                    </Stack>
                </AdaChatBotProvider>
            </SmallContentContainer>
        </PageLayout>
    );
};

const SecurityDashboardSection = ({ title, children }: React.PropsWithChildren<{ title: JSX.Element }>) => (
    <Stack vertical spacing="200">
        <Typography textStyle="titleMediumStrong" as="h2">
            {title}
        </Typography>
        {children}
    </Stack>
);
